import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Trophy, Users, ChevronRight, ChevronLeft, Play, Check, Crown, Medal, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getAvatarUrl } from '../utils/avatarUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CREATOR_EMAIL = 'danilogallo16@gmail.com';

const FASCIA_NAMES = { 1: 'Esperto', 2: 'Avanzato', 3: 'Intermedio', 4: 'Base', 5: 'Principiante' };
const FASCIA_COLORS = {
  1: 'bg-amber-500', 2: 'bg-purple-500', 3: 'bg-sky-500', 4: 'bg-emerald-500', 5: 'bg-slate-400'
};

const TournamentDetailPage = () => {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resultModal, setResultModal] = useState(null);
  const [sets, setSets] = useState([{ p1: '', p2: '' }, { p1: '', p2: '' }]);
  const [winnerId, setWinnerId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isCreator = user?.email === CREATOR_EMAIL;
  const isJoined = tournament?.participants?.some(p => p.user_id === user?.user_id);

  const fetchTournament = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tournaments/${tournamentId}`, { withCredentials: true });
      setTournament(res.data);
    } catch { toast.error('Errore nel caricamento'); navigate('/tournaments'); }
    finally { setLoading(false); }
  }, [tournamentId, navigate]);

  useEffect(() => { fetchTournament(); }, [fetchTournament]);

  const handleJoin = async () => {
    try {
      await axios.post(`${API_URL}/api/tournaments/${tournamentId}/join`, {}, { withCredentials: true });
      toast.success('Iscrizione completata!');
      fetchTournament();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleLeave = async () => {
    try {
      await axios.post(`${API_URL}/api/tournaments/${tournamentId}/leave`, {}, { withCredentials: true });
      toast.success('Iscrizione annullata');
      fetchTournament();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleGenerateBracket = async () => {
    try {
      await axios.post(`${API_URL}/api/tournaments/${tournamentId}/generate-bracket`, {}, { withCredentials: true });
      toast.success('Tabellone generato!');
      fetchTournament();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare questo torneo?')) return;
    try {
      await axios.delete(`${API_URL}/api/tournaments/${tournamentId}`, { withCredentials: true });
      toast.success('Torneo eliminato');
      navigate('/tournaments');
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const openResultModal = (match) => {
    setResultModal(match);
    setWinnerId('');
    setSets([{ p1: '', p2: '' }, { p1: '', p2: '' }]);
  };

  const handleSubmitResult = async () => {
    if (!winnerId) { toast.error('Seleziona il vincitore'); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/tournaments/${tournamentId}/matches/${resultModal.match_num}/result`,
        { winner_id: winnerId, sets: sets.filter(s => s.p1 !== '' && s.p2 !== '').map(s => ({ p1: parseInt(s.p1), p2: parseInt(s.p2) })) },
        { withCredentials: true }
      );
      toast.success('Risultato registrato!');
      setResultModal(null);
      fetchTournament();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center min-h-screen"><div className="animate-pulse text-slate-400">Caricamento...</div></div>;
  }

  if (!tournament) return null;

  const rounds = {};
  (tournament.bracket || []).forEach(m => {
    if (!rounds[m.round]) rounds[m.round] = [];
    rounds[m.round].push(m);
  });
  const totalRounds = tournament.total_rounds || 0;

  const getRoundName = (r) => {
    if (r === totalRounds) return 'Finale';
    if (r === totalRounds - 1) return 'Semifinale';
    if (r === totalRounds - 2) return 'Quarti';
    return `Round ${r}`;
  };

  const PlayerSlot = ({ player, isWinner, isBye }) => {
    if (isBye) return <div className="h-10 flex items-center px-3 text-xs text-slate-300 italic">BYE</div>;
    if (!player) return <div className="h-10 flex items-center px-3 text-xs text-slate-300">In attesa...</div>;
    return (
      <div className={`h-10 flex items-center gap-2 px-3 rounded-lg transition-all ${isWinner ? 'bg-amber-50 font-semibold' : ''}`}>
        <div className={`w-6 h-6 ${FASCIA_COLORS[player.fascia] || 'bg-slate-400'} rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 overflow-hidden`}>
          {player.picture_path || player.picture ? (
            <img src={getAvatarUrl(player)} alt="" className="w-full h-full object-cover rounded-full" />
          ) : player.name?.charAt(0)}
        </div>
        <span className="text-xs text-slate-800 truncate flex-1">{player.name}</span>
        {player.seed && <span className="text-[10px] text-slate-400">[{player.seed}]</span>}
        {isWinner && <Crown className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto animate-fadeIn" data-testid="tournament-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/tournaments')} className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="font-outfit text-2xl font-semibold text-slate-900">{tournament.name}</h1>
          <p className="text-slate-500 text-sm">{tournament.gender === 'M' ? 'Maschile' : 'Femminile'} — {tournament.participants?.length || 0} iscritti</p>
        </div>
        {isCreator && tournament.status === 'registration' && (
          <button onClick={handleDelete} data-testid="delete-tournament-btn" className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 hover:bg-red-100 text-red-500">
            <Trash2 className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Winner banner */}
      {tournament.status === 'completed' && tournament.winner_id && (
        <div className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 rounded-2xl p-5 mb-6 text-center" data-testid="winner-banner">
          <Crown className="w-10 h-10 text-white mx-auto mb-2" />
          <p className="text-white font-bold text-lg">Vincitore del Torneo!</p>
          <p className="text-white/90 text-sm">
            {tournament.participants?.find(p => p.user_id === tournament.winner_id)?.name || 'Vincitore'}
            <span className="block text-xs text-white/70 mt-1">+500 punti ranking</span>
          </p>
          {tournament.finalist_id && (
            <p className="text-white/80 text-xs mt-2 flex items-center justify-center gap-1">
              <Medal className="w-3.5 h-3.5" />
              Finalista: {tournament.participants?.find(p => p.user_id === tournament.finalist_id)?.name || ''}
              <span className="text-white/60">(+200 pts)</span>
            </p>
          )}
        </div>
      )}

      {/* Actions */}
      {tournament.status === 'registration' && (
        <div className="space-y-3 mb-6">
          {!isJoined && (
            <button onClick={handleJoin} data-testid="join-tournament-btn"
              className="w-full py-3.5 bg-amber-500 text-white rounded-full font-medium hover:bg-amber-600 transition-all flex items-center justify-center gap-2">
              <Users className="w-5 h-5" /> Iscriviti al Torneo
            </button>
          )}
          {isJoined && (
            <button onClick={handleLeave} data-testid="leave-tournament-btn"
              className="w-full py-3.5 bg-white text-red-500 border border-red-200 rounded-full font-medium hover:bg-red-50 transition-all">
              Annulla Iscrizione
            </button>
          )}
          {isCreator && tournament.participants?.length >= 2 && (
            <button onClick={handleGenerateBracket} data-testid="generate-bracket-btn"
              className="w-full py-3.5 bg-emerald-500 text-white rounded-full font-medium hover:bg-emerald-600 transition-all flex items-center justify-center gap-2">
              <Play className="w-5 h-5" /> Genera Tabellone e Inizia
            </button>
          )}
        </div>
      )}

      {/* Participants */}
      {tournament.status === 'registration' && (
        <div className="bg-white rounded-2xl p-5 border border-slate-100 mb-6">
          <h3 className="font-outfit font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Users className="w-5 h-5 text-amber-500" /> Iscritti ({tournament.participants?.length || 0})
          </h3>
          {(!tournament.participants || tournament.participants.length === 0) ? (
            <p className="text-sm text-slate-400 text-center py-4">Nessun iscritto ancora</p>
          ) : (
            <div className="space-y-2">
              {tournament.participants.map((p, i) => (
                <div key={p.user_id} className="flex items-center gap-3 py-2 px-3 rounded-xl bg-slate-50">
                  <span className="text-xs text-slate-400 font-medium w-6">{i + 1}</span>
                  <div className={`w-8 h-8 ${FASCIA_COLORS[p.fascia] || 'bg-slate-400'} rounded-full flex items-center justify-center text-white text-xs font-bold overflow-hidden`}>
                    {getAvatarUrl(p) ? <img src={getAvatarUrl(p)} alt="" className="w-full h-full object-cover rounded-full" /> : p.name?.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{p.name}</p>
                    <p className="text-xs text-slate-500">{FASCIA_NAMES[p.fascia]} — {p.ranking_points} pts</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bracket - Round by Round Navigation */}
      {(tournament.status === 'active' || tournament.status === 'completed') && tournament.bracket?.length > 0 && (
        <BracketView
          rounds={rounds}
          totalRounds={totalRounds}
          getRoundName={getRoundName}
          isCreator={isCreator}
          openResultModal={openResultModal}
          PlayerSlot={PlayerSlot}
        />
      )}

      {/* Result Modal */}
      {resultModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setResultModal(null)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4">Risultato Partita</h3>

            <div className="space-y-3 mb-5">
              <p className="text-sm text-slate-600 font-medium">Vincitore:</p>
              {[resultModal.player_a, resultModal.player_b].map(p => p && (
                <button
                  key={p.user_id}
                  onClick={() => setWinnerId(p.user_id)}
                  data-testid={`select-winner-${p.user_id}`}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                    winnerId === p.user_id ? 'border-amber-400 bg-amber-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className={`w-8 h-8 ${FASCIA_COLORS[p.fascia]} rounded-full flex items-center justify-center text-white text-xs font-bold`}>
                    {p.name?.charAt(0)}
                  </div>
                  <span className="text-sm font-medium text-slate-900">{p.name}</span>
                  {winnerId === p.user_id && <Check className="w-5 h-5 text-amber-500 ml-auto" />}
                </button>
              ))}
            </div>

            <div className="mb-5">
              <p className="text-sm text-slate-600 font-medium mb-2">Punteggio Set:</p>
              {sets.map((s, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-slate-400 w-12">{i === 2 ? 'TB' : `Set ${i + 1}`}</span>
                  <input type="number" min="0" max="13" value={s.p1} onChange={e => { const n = [...sets]; n[i].p1 = e.target.value; setSets(n); }}
                    className="w-16 px-3 py-2 rounded-xl border border-slate-200 text-center text-sm" placeholder="-" />
                  <span className="text-slate-300">-</span>
                  <input type="number" min="0" max="13" value={s.p2} onChange={e => { const n = [...sets]; n[i].p2 = e.target.value; setSets(n); }}
                    className="w-16 px-3 py-2 rounded-xl border border-slate-200 text-center text-sm" placeholder="-" />
                </div>
              ))}
              {sets.length < 3 && (
                <button onClick={() => setSets([...sets, { p1: '', p2: '' }])} className="text-xs text-amber-600 font-medium mt-1">
                  + Aggiungi Tiebreak
                </button>
              )}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setResultModal(null)} className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-full font-medium">Annulla</button>
              <button onClick={handleSubmitResult} disabled={submitting || !winnerId} data-testid="submit-result-btn"
                className="flex-1 py-3 bg-amber-500 text-white rounded-full font-medium disabled:opacity-50">
                {submitting ? 'Salvando...' : 'Conferma'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BracketView = ({ rounds, totalRounds, getRoundName, isCreator, openResultModal, PlayerSlot }) => {
  const [activeRound, setActiveRound] = useState(() => {
    // Default to first round with unplayed matches, or last round
    for (let r = 1; r <= totalRounds; r++) {
      if ((rounds[r] || []).some(m => !m.completed && !m.is_bye)) return r;
    }
    return totalRounds;
  });

  const roundMatches = rounds[activeRound] || [];
  const completedInRound = roundMatches.filter(m => m.completed || m.is_bye).length;
  const totalInRound = roundMatches.length;

  return (
    <div className="mb-6" data-testid="bracket-view">
      <h3 className="font-outfit font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <Trophy className="w-5 h-5 text-amber-500" /> Tabellone
      </h3>

      {/* Round Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => setActiveRound(r => Math.max(1, r - 1))}
          disabled={activeRound === 1}
          data-testid="bracket-prev-round"
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-5 h-5 text-slate-600" />
        </button>

        <div className="flex gap-1.5 items-center">
          {Array.from({ length: totalRounds }, (_, i) => i + 1).map(r => {
            const rMatches = rounds[r] || [];
            const allDone = rMatches.every(m => m.completed || m.is_bye);
            return (
              <button
                key={r}
                onClick={() => setActiveRound(r)}
                data-testid={`bracket-round-tab-${r}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  activeRound === r
                    ? r === totalRounds ? 'bg-amber-500 text-white' : 'bg-slate-800 text-white'
                    : allDone ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
              >
                {getRoundName(r)}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => setActiveRound(r => Math.min(totalRounds, r + 1))}
          disabled={activeRound === totalRounds}
          data-testid="bracket-next-round"
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight className="w-5 h-5 text-slate-600" />
        </button>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${(completedInRound / totalInRound) * 100}%` }} />
        </div>
        <span className="text-xs text-slate-500 font-medium">{completedInRound}/{totalInRound}</span>
      </div>

      {/* Matches for active round */}
      <div className="space-y-3">
        {roundMatches.map((match, idx) => {
          const canSubmit = isCreator && !match.completed && match.player_a && match.player_b && !match.is_bye;
          return (
            <div
              key={match.match_num}
              data-testid={`bracket-match-${match.match_num}`}
              className={`bg-white rounded-2xl border overflow-hidden transition-all ${
                match.completed ? 'border-amber-200' : canSubmit ? 'border-emerald-300 cursor-pointer hover:shadow-md active:scale-[0.98]' : 'border-slate-100'
              }`}
              onClick={() => canSubmit && openResultModal(match)}
            >
              {/* Match number label */}
              <div className="px-3 py-1 bg-slate-50 flex items-center justify-between">
                <span className="text-[10px] text-slate-400 font-medium">Partita {idx + 1}</span>
                {match.completed && !match.is_bye && (
                  <span className="text-[10px] text-emerald-600 font-medium">Completata</span>
                )}
                {match.is_bye && (
                  <span className="text-[10px] text-slate-400 italic">BYE</span>
                )}
                {canSubmit && (
                  <span className="text-[10px] text-emerald-600 font-semibold animate-pulse">Inserisci Risultato</span>
                )}
              </div>
              <PlayerSlot player={match.player_a} isWinner={match.winner_id === match.player_a?.user_id} />
              <div className="h-px bg-slate-100" />
              <PlayerSlot player={match.player_b} isWinner={match.winner_id === match.player_b?.user_id} isBye={match.is_bye} />
              {match.sets?.length > 0 && (
                <div className="bg-slate-50 px-3 py-1.5 text-center">
                  <span className="text-xs text-slate-600 font-mono font-medium">
                    {match.sets.map(s => `${s.p1}-${s.p2}`).join('  ')}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TournamentDetailPage;
